import * as React from "react";
import { useEffect, useRef, useId } from "react";
import { Chart, registerables, type ChartConfiguration, type ChartData } from "chart.js";
import { cn } from "~/lib/utils";
import { CssHelper } from "~/helpers/css_helper";

Chart.register(...registerables);

export type ChartConfig = {
  [key: string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
    theme?: { light: string; dark: string };
  };
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}

export type ChartContainerProps = React.ComponentProps<"div"> & {
  config: ChartConfig;
  children: React.ReactNode;
};

export function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}: ChartContainerProps) {
  const uniqueId = useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        className={cn("position-relative w-100", className)}
        style={
          {
            "--color-background": "var(--colors-white)",
            ...Object.entries(config).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [`--color-${key}`]: value.theme
                  ? `var(--color-${key}-value, ${value.theme.light})`
                  : value.color,
              }),
              {}
            ),
          } as React.CSSProperties
        }
        {...props}
      >
        {children}
      </div>
    </ChartContext.Provider>
  );
}

export type BarChartDataset = {
  dataKey: string;
  stackId?: string;
  radius?: number | { topLeft?: number; topRight?: number; bottomLeft?: number; bottomRight?: number };
  fill?: string | string[];
  borderColor?: string | string[];
  borderWidth?: number | { top?: number; right?: number; bottom?: number; left?: number };
  order?: number;
};

export type BarChartProps = {
  data: Array<Record<string, unknown>>;
  xAxisKey?: string;
  datasets: BarChartDataset[];
  height?: number | string;
  className?: string;
  stacked?: boolean;
  showGrid?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showTooltip?: boolean;
  yAxisMax?: number;
};

export function BarChart({
  data,
  xAxisKey = "label",
  datasets,
  height,
  className,
  stacked = false,
  showGrid = false,
  showXAxis = false,
  showYAxis = false,
  showTooltip = false,
  yAxisMax,
}: BarChartProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const { config } = useChart();

  useEffect(() => {
    if (!canvasRef.current || !data.length) return;

    const labels = data.map((d) => String(d[xAxisKey] || ""));

    const chartDatasets = datasets.map((dataset) => {
      const fill = dataset.fill || config[dataset.dataKey]?.color || "var(--colors-grey-500)";
      const backgroundColor = Array.isArray(fill) ? fill : fill;

      return {
        label: String(config[dataset.dataKey]?.label || dataset.dataKey),
        data: data.map((d) => Number(d[dataset.dataKey] || 0)),
        backgroundColor,
        borderColor: dataset.borderColor,
        borderWidth: dataset.borderWidth,
        borderRadius: dataset.radius,
        stack: stacked ? (dataset.stackId || "stack") : undefined,
        order: dataset.order,
      };
    });

    const chartOptions: ChartConfiguration<"bar">["options"] = {
      animation: false,
      maintainAspectRatio: false,
      layout: { padding: { left: 0, right: 0, top: 0, bottom: -8 } },
      interaction: { mode: "index", intersect: false },
      scales: {
        x: {
          stacked,
          grid: { display: showGrid },
          ticks: { display: showXAxis },
          border: { display: showXAxis },
        },
        y: {
          stacked,
          grid: {
            color: (context) => {
              if(context.tick.value === 0) {
                return CssHelper.getVar("--colors-grey-200");
              }

              return false
            }
          },
          border: { display: showYAxis },
          beginAtZero: true,
          max: yAxisMax,
          ticks: { display: showYAxis },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: showTooltip },
      },
    };

    const chartData: ChartData<"bar"> = {
      labels,
      datasets: chartDatasets,
    };

    const chart = new Chart(canvasRef.current, {
      type: "bar",
      data: chartData,
      options: chartOptions,
    });

    return () => chart.destroy();
  }, [data, datasets, config, xAxisKey, stacked, showGrid, showXAxis, showYAxis, showTooltip, yAxisMax]);

  return (
    <div className={cn("h-chart", className)} style={height ? { height } : undefined}>
      <canvas ref={canvasRef} />
    </div>
  );
}

export { useChart };

